import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  LLM_PROVIDERS,
  STT_PROVIDERS,
  TTS_PROVIDERS,
  VOICE_GENDERS,
  VOICE_LOCALE_KEYS,
} from 'components/@admin/@ai/aiConfigConstants';
import Loading from 'components/common/Loading';
import { string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';
import {
  useGetCompanyAiConfigQuery,
  usePatchCompanyAiConfigMutation,
} from 'store/app/entities/AiConfigSlice';

const CompanyAiOverridePanel = ({ companyId }) => {
  const { data, isLoading, isError } = useGetCompanyAiConfigQuery(companyId, { skip: !companyId });
  const [patchCompany, { isLoading: saving }] = usePatchCompanyAiConfigMutation();
  const [draft, setDraft] = useState({});
  const [inherit, setInherit] = useState({
    llmProvider: true,
    defaultModelKey: true,
    sttProvider: true,
    ttsProvider: true,
  });
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const cfg = data?.company?.aiConfig || {};
    setDraft({
      llmProvider: cfg.llmProvider || '',
      defaultModelKey: cfg.defaultModelKey || '',
      sttProvider: cfg.sttProvider || '',
      ttsProvider: cfg.ttsProvider || '',
      voices: cfg.voices || {},
    });
    setInherit({
      llmProvider: !cfg.llmProvider,
      defaultModelKey: !cfg.defaultModelKey,
      sttProvider: !cfg.sttProvider,
      ttsProvider: !cfg.ttsProvider,
    });
  }, [data?.company?.aiConfig, companyId]);

  const platformModels = useMemo(() => data?.effective?.models || {}, [data?.effective?.models]);
  const modelKeys = useMemo(() => Object.keys(platformModels), [platformModels]);

  const modelsForProvider = useMemo(() => {
    const provider = inherit.llmProvider
      ? data?.effective?.defaults?.llmProvider
      : draft.llmProvider;
    return modelKeys.filter(key => platformModels[key]?.provider === provider);
  }, [
    inherit.llmProvider,
    draft.llmProvider,
    data?.effective?.defaults?.llmProvider,
    modelKeys,
    platformModels,
  ]);

  const ttsProvider = inherit.ttsProvider
    ? data?.effective?.defaults?.ttsProvider
    : draft.ttsProvider;

  const handleSave = async () => {
    setFeedback(null);
    const body = {};
    if (inherit.llmProvider) body.llmProvider = null;
    else if (draft.llmProvider) body.llmProvider = draft.llmProvider;

    if (inherit.defaultModelKey) body.defaultModelKey = null;
    else if (draft.defaultModelKey) body.defaultModelKey = draft.defaultModelKey;

    if (inherit.sttProvider) body.sttProvider = null;
    else if (draft.sttProvider) body.sttProvider = draft.sttProvider;

    if (inherit.ttsProvider) body.ttsProvider = null;
    else if (draft.ttsProvider) body.ttsProvider = draft.ttsProvider;

    const voiceOverrides = draft.voices?.[ttsProvider];
    if (voiceOverrides && Object.keys(voiceOverrides).length) {
      body.voices = { [ttsProvider]: voiceOverrides };
    }

    try {
      await patchCompany({ companyId, body }).unwrap();
      setFeedback({ severity: 'success', message: I18n.t('CompanyAiOverridePanel.Saved') });
    } catch {
      setFeedback({ severity: 'error', message: I18n.t('CompanyAiOverridePanel.SaveError') });
    }
  };

  if (!companyId) return null;
  if (isLoading) return <Loading />;
  if (isError) {
    return <Alert severity="error">{I18n.t('CompanyAiOverridePanel.LoadError')}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
          }}
        >
          {I18n.t('CompanyAiOverridePanel.Title')}
        </Typography>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
          {I18n.t('CompanyForm.Save')}
        </Button>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Stack
        spacing={2}
        sx={{
          maxWidth: 520,
        }}
      >
        {[
          {
            key: 'llmProvider',
            label: I18n.t('AdminAiConfigScene.LlmProvider'),
            options: LLM_PROVIDERS,
          },
          {
            key: 'defaultModelKey',
            label: I18n.t('AdminAiConfigScene.DefaultModel'),
            options: modelsForProvider,
            isModel: true,
          },
          {
            key: 'sttProvider',
            label: I18n.t('AdminAiConfigScene.SttProvider'),
            options: STT_PROVIDERS,
          },
          {
            key: 'ttsProvider',
            label: I18n.t('AdminAiConfigScene.TtsProvider'),
            options: TTS_PROVIDERS,
          },
        ].map(({ key, label, options, isModel }) => (
          <Box key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inherit[key]}
                  onChange={e => setInherit(prev => ({ ...prev, [key]: e.target.checked }))}
                />
              }
              label={I18n.t('CompanyAiOverridePanel.Inherit', { field: label })}
            />
            {!inherit[key] && (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>{label}</InputLabel>
                <Select
                  label={label}
                  value={draft[key] || (isModel ? 'haiku' : options[0])}
                  onChange={e => setDraft(prev => ({ ...prev, [key]: e.target.value }))}
                >
                  {options.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {isModel ? platformModels[opt]?.label || opt : opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ))}
      </Stack>

      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        {I18n.t('CompanyAiOverridePanel.VoiceOverrides')}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mb: 2,
        }}
      >
        {I18n.t('CompanyAiOverridePanel.VoiceHint', { provider: ttsProvider || 'elevenlabs' })}
      </Typography>
      {VOICE_LOCALE_KEYS.map(localeKey => (
        <Box key={localeKey} sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
            }}
          >
            {localeKey}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            {VOICE_GENDERS.map(gender => (
              <TextField
                key={gender}
                size="small"
                label={gender}
                value={draft.voices?.[ttsProvider]?.[localeKey]?.[gender] || ''}
                onChange={e =>
                  setDraft(prev => ({
                    ...prev,
                    voices: {
                      ...prev.voices,
                      [ttsProvider]: {
                        ...(prev.voices?.[ttsProvider] || {}),
                        [localeKey]: {
                          ...(prev.voices?.[ttsProvider]?.[localeKey] || {}),
                          [gender]: e.target.value,
                        },
                      },
                    },
                  }))
                }
                sx={{ flex: 1 }}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};

CompanyAiOverridePanel.propTypes = {
  companyId: string,
};

export default CompanyAiOverridePanel;
